import { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '../lib/api.js';

// Submission review — one customer's clips with per-clip approve/reject/mark-used (v4 design).
//
// Layout
//   • Top    — back to inbox + keyboard help trigger
//   • Header — avatar, name, email, HubSpot link, status pill, bulk-pay CTA
//   • Stats  — Total payout (hero) + Clips / Approved / Used in ads
//   • Body   — 2-col on desktop: clip list (left), focused clip (right)
//
// Keyboard
//   A / R / U  — approve / reject / mark-used the active clip
//   J / K      — next / prev clip   (also ↓ / ↑)
//   ?          — show shortcuts overlay
//   Esc        — close overlay or cancel reject form
//
// All Worker wiring is unchanged from v3:
//   • GET  /api/dashboard/submissions/:id
//   • POST /api/clip/:id/{approve|reject|mark-used}   (with reviewer_email)
//   • POST /api/submission/:id/approve                (bulk-approve & pay)
//
// Clip-status mapping from the schema enum to design pills:
//   recording        — never shown here (still in customer's hands)
//   uploaded         → "New"      (.pill-new — orange)
//   approved         → "Approved" (.pill-approved — green)
//   used_in_marketing+approved → "Used (+$20)" (.pill-used — orange gradient)
//   rejected         → "Rejected" (.pill-rejected — red)
//   redo_requested   → "Redo requested" (gray)
//
// The schema stores `used_in_marketing` as a separate boolean — it's NOT
// a clip.status value. So derived pill: if used_in_marketing && approved → 'used'.

const PILL_META = {
  uploaded:       { label: 'New',           cls: 'pill-new'      },
  approved:       { label: 'Approved',      cls: 'pill-approved' },
  used:           { label: 'Used in ad',    cls: 'pill-used'     },
  rejected:       { label: 'Rejected',      cls: 'pill-rejected' },
  redo_requested: { label: 'Redo requested',cls: 'pill-gray'     },
  recording:      { label: 'Recording',     cls: 'pill-gray'     },
};
function clipPill(c) {
  if (c.status === 'approved' && c.used_in_marketing) return PILL_META.used;
  return PILL_META[c.status] || { label: c.status, cls: 'pill-gray' };
}

// AI quality badge -- maps the 1-10 score from clip_verifications into a
// visual tier. Same thresholds as the Slack post so the UI and Slack
// agree on what counts as "good/ok/bad". Returns null if there's no
// verification yet (Stream may still be encoding, or the clip predates
// the verification system).
function verificationBadge(v) {
  if (!v) return null;
  if (v.error) return { tier: 'error', label: 'AI: error', score: null, summary: v.error };
  const s = v.score;
  let tier;
  if (s >= 8) tier = 'good';
  else if (s >= 5) tier = 'fair';
  else tier = 'poor';
  return {
    tier,
    label: `AI ${s}/10`,
    score: s,
    summary: v.summary || '',
    issues: v.issues || [],
    person_visible: v.person_visible,
    audio_clear: v.audio_clear,
    content_match: v.content_match,
    ran_at: v.ran_at,
    model: v.model,
  };
}

// Maps a payout's live Tremendous reward status to a display badge.
// reward_status: CART | DELIVERED | REDEEMED ; delivery_status: SUCCEEDED etc.
function rewardBadge(p) {
  if (p.redeemed || p.reward_status === 'REDEEMED') {
    return { cls: 'pill-approved', label: 'Redeemed' };
  }
  if (p.reward_status === 'DELIVERED' || p.delivery_status === 'SUCCEEDED') {
    return { cls: 'pill-new', label: 'Delivered · not yet redeemed' };
  }
  if (p.reward_status === 'CART' || p.status === 'pending') {
    return { cls: 'pill-gray', label: 'Sent · awaiting delivery' };
  }
  if (p.status === 'failed') return { cls: 'pill-rejected', label: 'Failed' };
  return { cls: 'pill-gray', label: p.reward_status || p.status || 'unknown' };
}

const SUBMISSION_PILL = {
  submitted:   { label: 'Waiting for review',    cls: 'pill-new'      },
  approved:    { label: 'Approved',             cls: 'pill-approved' },
  paid:        { label: 'Paid',                  cls: 'pill-approved' },
  partial_approved: { label: 'Partially approved', cls: 'pill-approved' },
  rejected:    { label: 'Rejected',              cls: 'pill-rejected' },
  in_progress: { label: 'In progress',           cls: 'pill-gray'     },
};

// Avatar gradient — same palette as the inbox. Kept inline rather than
// imported because Vite-extracting tiny helpers ends up costing more in
// indirection than the duplication.
const AVATAR_PALETTES = [
  ['#c8d6fa', 'var(--ravepilot-orange)'],
  ['#7FD7AA', '#1A7A4D'],
  ['#F2A6BB', '#D43A6F'],
  ['#c8d6fa', '#F5B400'],
  ['#A4B7FF', '#6461FC'],
  ['#8aa0f4', 'var(--ravepilot-orange)'],
];
function avatarStyle(name) {
  let h = 0;
  for (let i = 0; i < (name || '').length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
  const [a, b] = AVATAR_PALETTES[h % AVATAR_PALETTES.length];
  return { background: `linear-gradient(135deg, ${a}, ${b})` };
}

// Payout rule — base $10/approved clip, +$20 bonus when marketing-used.
// Matches the Worker's tremendous-order math (src/index.js line ~1190).
const BASE_PAYOUT_CENTS  = 1000;
const USAGE_BONUS_CENTS  = 2000;

export default function DashboardSubmission() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [reviewer, setReviewer] = useState(() => localStorage.getItem('vt-reviewer-email') || '');
  const [activeClipId, setActiveClipId] = useState(null);
  const [showRejectFor, setShowRejectFor] = useState(null);
  const [rejectNote, setRejectNote] = useState('');
  const [showShortcuts, setShowShortcuts] = useState(false);

  // Persist reviewer email so subsequent actions on the same machine
  // don't re-prompt. Matches v3 behavior.
  useEffect(() => {
    if (reviewer) localStorage.setItem('vt-reviewer-email', reviewer);
  }, [reviewer]);

  // The session already knows who's logged in (magic-link email). Use it as
  // the reviewer automatically so we don't ask again. Runs once; if the user
  // explicitly clicks "change" we don't snap it back.
  const triedWhoami = useRef(false);
  useEffect(() => {
    if (reviewer || triedWhoami.current) return;
    triedWhoami.current = true;
    let cancelled = false;
    (async () => {
      try {
        const me = await api('/api/admin/whoami');
        if (!cancelled && me?.email) setReviewer(me.email);
      } catch { /* fall back to the manual prompt */ }
    })();
    return () => { cancelled = true; };
  }, [reviewer]);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const r = await api(`/api/dashboard/submissions/${id}`);
      setData(r);
      // Set the active clip to the first uploaded (pending review) clip,
      // falling back to the first clip overall. Lands the reviewer on
      // something actionable when they open a submission cold.
      if (!activeClipId) {
        const firstPending = (r.clips || []).find((c) => c.status === 'uploaded');
        setActiveClipId(firstPending?.id || r.clips?.[0]?.id || null);
      }
    } catch (e) {
      if (e.status === 401) { navigate('/dashboard/login'); return; }
      setError(e.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [id, navigate, activeClipId]);

  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Per-clip actions ──────────────────────────────────────────────────
  const clipAction = useCallback(async (clipId, action, extra = {}) => {
    if (busy) return;
    setBusy(true);
    try {
      await api(`/api/clip/${clipId}/${action}`, {
        method: 'POST',
        body: { reviewer_email: reviewer || 'unknown', ...extra },
      });
      await load();
    } catch (e) {
      alert(e.message || `${action} failed`);
    } finally {
      setBusy(false);
    }
  }, [busy, reviewer, load]);

  const approve   = (clipId) => clipAction(clipId, 'approve');
  const markUsed  = (clipId) => clipAction(clipId, 'mark-used');
  const submitReject = async (clipId) => {
    await clipAction(clipId, 'reject', { notes: rejectNote.trim() || null });
    setShowRejectFor(null);
    setRejectNote('');
  };

  const bulkApprove = async () => {
    if (busy || !data) return;
    const approvedCount = data.clips.filter((c) => c.status === 'approved').length;
    if (approvedCount === 0) {
      alert('Approve at least one clip first.');
      return;
    }
    if (!confirm(`Approve this submission and email the customer their free month of RavePilot? This is irreversible.`)) return;
    setBusy(true);
    try {
      const r = await api(`/api/submission/${data.submission.id}/approve`, {
        method: 'POST',
        body: { reviewer_email: reviewer || 'unknown' },
      });
      alert(`Approved. A free month email is on its way to ${data.submission.customer_email}.`);
      await load();
    } catch (e) {
      alert(e.message || 'Approval failed');
    } finally {
      setBusy(false);
    }
  };

  // ── Keyboard shortcuts ────────────────────────────────────────────────
  // Bind once and read latest state via the ref-y pattern. Skip when the
  // user is typing in an input/textarea so we don't hijack their keys.
  useEffect(() => {
    function onKey(e) {
      const t = e.target;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
      if (!data) return;
      const clips = data.clips || [];
      const clip = clips.find((c) => c.id === activeClipId);
      if (e.key === '?' || (e.key === '/' && e.shiftKey)) {
        e.preventDefault(); setShowShortcuts((v) => !v); return;
      }
      if (e.key === 'Escape') { setShowShortcuts(false); setShowRejectFor(null); return; }
      if (!clip) return;
      if (e.key === 'a' || e.key === 'A') {
        if (clip.status === 'uploaded') { e.preventDefault(); approve(clip.id); }
      } else if (e.key === 'r' || e.key === 'R') {
        if (clip.status === 'uploaded' || clip.status === 'approved') {
          e.preventDefault(); setShowRejectFor(clip.id);
        }
      } else if (e.key === 'u' || e.key === 'U') {
        if (clip.status === 'approved' && !clip.used_in_marketing) {
          e.preventDefault(); markUsed(clip.id);
        }
      } else if (e.key === 'j' || e.key === 'J' || e.key === 'ArrowDown') {
        e.preventDefault();
        const idx = clips.findIndex((c) => c.id === activeClipId);
        const next = clips[Math.min(clips.length - 1, idx + 1)];
        if (next) setActiveClipId(next.id);
      } else if (e.key === 'k' || e.key === 'K' || e.key === 'ArrowUp') {
        e.preventDefault();
        const idx = clips.findIndex((c) => c.id === activeClipId);
        const prev = clips[Math.max(0, idx - 1)];
        if (prev) setActiveClipId(prev.id);
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [data, activeClipId, approve, markUsed]);

  // ── Loading / error / not-found ───────────────────────────────────────
  if (loading) {
    return (
      <main className="admin-page admin-review-page">
        <div className="review-back">
          <button className="link-button" onClick={() => navigate('/dashboard')}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 6 }}>
              <polyline points="15 18 9 12 15 6"/>
            </svg>
            All reviews
          </button>
        </div>
        <div className="admin-empty">
          <div className="admin-empty-text"><strong>Loading…</strong></div>
        </div>
      </main>
    );
  }

  if (error || !data) {
    return (
      <main className="admin-page admin-review-page">
        <div className="review-back">
          <button className="link-button" onClick={() => navigate('/dashboard')}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 6 }}>
              <polyline points="15 18 9 12 15 6"/>
            </svg>
            All reviews
          </button>
        </div>
        <div className="admin-empty">
          <div className="admin-empty-ico" style={{ borderColor: '#FECDD3', color: '#B42318' }}>
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
            </svg>
          </div>
          <div className="admin-empty-text">
            <strong>{error || 'Submission not found.'}</strong>
            <span>It may have been deleted, or your session may have expired.</span>
          </div>
        </div>
      </main>
    );
  }

  const { submission, clips, scripts, payouts = [] } = data;
  const scriptByKey = Object.fromEntries((scripts || []).map((s) => [s.key, s]));
  const scriptTitle = (key) => scriptByKey[key]?.title || key;

  const activeClip   = clips.find((c) => c.id === activeClipId) || clips[0] || null;
  const activeIndex  = clips.findIndex((c) => c.id === activeClipId);

  const clipsApproved = clips.filter((c) => c.status === 'approved').length;
  const clipsUsed     = clips.filter((c) => c.used_in_marketing).length;
  const clipsRejected = clips.filter((c) => c.status === 'rejected').length;

  // Expected payout = $10 × approved + $20 × used. Approved clips include
  // those already marked-used so we don't double-count, but the bonus is
  // additive on top of the base — same math the Worker uses.
  const expectedPayoutCents = clipsApproved * BASE_PAYOUT_CENTS + clipsUsed * USAGE_BONUS_CENTS;
  const expectedPayoutLabel = (expectedPayoutCents / 100).toFixed(0);

  const subPill = SUBMISSION_PILL[submission.status] || { label: submission.status, cls: 'pill-gray' };

  return (
    <main className="admin-page admin-review-page">
      {/* ── Top: back + kbd-help trigger ────────────────────────────────── */}
      <div className="review-back">
        <button className="link-button" onClick={() => navigate('/dashboard')}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 6 }}>
            <polyline points="15 18 9 12 15 6"/>
          </svg>
          All reviews
        </button>
        <button
          className="kbd-help-trigger"
          onClick={() => setShowShortcuts((v) => !v)}
          aria-label="Keyboard shortcuts"
          title="Keyboard shortcuts"
        >
          <kbd>?</kbd>
        </button>
      </div>

      {/* ── Shortcuts overlay ──────────────────────────────────────────── */}
      {showShortcuts && (
        <div className="kbd-help" onClick={() => setShowShortcuts(false)}>
          <div className="kbd-help-card" onClick={(e) => e.stopPropagation()}>
            <div className="kbd-help-head">
              <h3>Keyboard shortcuts</h3>
              <button onClick={() => setShowShortcuts(false)} className="kbd-help-close" aria-label="Close">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><path d="M6 6l12 12M6 18L18 6"/></svg>
              </button>
            </div>
            <ul className="kbd-help-list">
              <li><kbd>A</kbd><span>Approve clip</span></li>
              <li><kbd>R</kbd><span>Reject clip</span></li>
              <li><kbd>U</kbd><span>Mark approved clip as used in an ad</span></li>
              <li><kbd>J</kbd> <kbd>K</kbd><span>Next / previous clip</span></li>
              <li><kbd>↑</kbd> <kbd>↓</kbd><span>Next / previous clip</span></li>
              <li><kbd>?</kbd><span>Show this</span></li>
              <li><kbd>Esc</kbd><span>Close</span></li>
            </ul>
          </div>
        </div>
      )}

      {/* ── Reviewer identity ──────────────────────────────────────────── */}
      {/* The session already knows who's logged in, so we capture the
          reviewer silently for the audit trail. Only prompt if we somehow
          couldn't determine it. */}
      {!reviewer && (
        <div className="review-reject-form" style={{ marginTop: 0, marginBottom: 16 }}>
          <label className="review-reject-label">Who&apos;s reviewing? (logged on every action)</label>
          <input
            type="email"
            className="review-reject-input"
            style={{ minHeight: 'auto', height: 44 }}
            placeholder="you@ravepilot.com"
            onBlur={(e) => setReviewer(e.target.value.trim())}
            defaultValue={reviewer}
          />
        </div>
      )}

      {/* ── Customer header ────────────────────────────────────────────── */}
      <div className="review-header">
        <div className="review-header-left">
          {submission.customer_photo_url ? (
            <div className="review-avatar" style={{ background: 'transparent' }}>
              <img
                src={submission.customer_photo_url}
                alt=""
                style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 14 }}
              />
            </div>
          ) : (
            <div className="review-avatar" aria-hidden="true" style={avatarStyle(submission.customer_name || submission.customer_email)}>
              {(submission.customer_name || submission.customer_email || '?').split(/\s+/).map((p) => p[0]).slice(0, 2).join('').toUpperCase()}
            </div>
          )}
          <div className="review-header-text">
            <h1 className="review-customer">{submission.customer_name || submission.customer_email}</h1>
            <a className="review-email" href={`mailto:${submission.customer_email}`}>{submission.customer_email}</a>
            <div className="review-meta">
              <span className={'pill ' + subPill.cls}>{subPill.label}</span>
              {submission.hubspot_contact_id && (
                <a
                  className="review-hubspot"
                  href={`https://app.hubspot.com/contacts/_/contact/${submission.hubspot_contact_id}`}
                  target="_blank"
                  rel="noopener"
                  title="Open in HubSpot"
                >
                  HubSpot #{submission.hubspot_contact_id}
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ marginLeft: 3 }}>
                    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
                    <polyline points="15 3 21 3 21 9"/>
                    <line x1="10" y1="14" x2="21" y2="3"/>
                  </svg>
                </a>
              )}
            </div>
          </div>
        </div>
        <div className="review-header-actions">
          {clipsApproved > 0 && submission.status === 'submitted' && (
            <button
              className="review-payout-btn"
              disabled={busy}
              onClick={bulkApprove}
            >
              {busy ? 'Working…' : 'Approve all & send free month'}
            </button>
          )}
          {(submission.status === 'approved' || submission.status === 'paid') && (
            <button className="review-payout-btn" disabled>
              ✓ Free month sent
            </button>
          )}
        </div>
      </div>

      {/* ── Stats strip ────────────────────────────────────────────────── */}
      <div className="review-stats">
        <div className="stat-cell stat-hero">
          <span className="stat-label">Reward</span>
          <span className="stat-num stat-num-hero">Free month</span>
          <span className="stat-foot">
            {clipsApproved > 0
              ? `${clipsApproved} clip${clipsApproved > 1 ? 's' : ''} approved`
              : 'Awaiting approval'}
          </span>
        </div>
        <div className="stat-cell">
          <span className="stat-label">Clips</span>
          <span className="stat-num">{clips.length}</span>
        </div>
        <div className="stat-cell">
          <span className="stat-label">Approved</span>
          <span className="stat-num">{clipsApproved}</span>
        </div>
        <div className="stat-cell">
          <span className="stat-label">Used in ads</span>
          <span className="stat-num">{clipsUsed}</span>
        </div>
      </div>

      {/* ── Rewards: real payout records + live redemption status ───────── */}
      {payouts.length > 0 && (
        <div className="review-rewards">
          <h2 className="review-side-title">Rewards · {payouts.length}</h2>
          <div className="review-rewards-list">
            {payouts.map((p) => {
              const badge = rewardBadge(p);
              const kind = p.payout_type === 'usage_bonus' ? 'Usage bonus' : 'Approval';
              return (
                <div key={p.id} className="review-reward-row">
                  <div className="review-reward-main">
                    <span className="review-reward-amt">${(p.amount_cents / 100).toFixed(2)}</span>
                    <span className="review-reward-kind">{kind}</span>
                    <span className={'pill ' + badge.cls}>{badge.label}</span>
                  </div>
                  <div className="review-reward-meta">
                    <span>{p.recipient_email}</span>
                    {p.tremendous_order_id && <span>· order {p.tremendous_order_id}</span>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Body: clip list + main panel ───────────────────────────────── */}
      <div className="review-body">
        <aside className="review-side">
          <h2 className="review-side-title">Clips · {clips.length}</h2>
          <div className="review-clip-list">
            {clips.map((c, i) => {
              const pill = clipPill(c);
              const badge = verificationBadge(c.verification);
              const isActive = c.id === activeClipId;
              return (
                <button
                  key={c.id}
                  className={'review-clip-row' + (isActive ? ' active' : '')}
                  onClick={() => {
                    setActiveClipId(c.id);
                    // On stacked mobile layout the player sits above the clip
                    // list — without this, tapping a clip gives no visible
                    // feedback because the swap happens off-screen.
                    if (window.matchMedia('(max-width: 760px)').matches) {
                      requestAnimationFrame(() => {
                        document.querySelector('.review-player')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      });
                    }
                  }}
                  type="button"
                >
                  <div className="review-clip-thumb">
                    <span className="review-clip-num">{i + 1}</span>
                    {c.stream_thumbnail_url
                      ? <img src={c.stream_thumbnail_url} alt="" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                      : <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M7 4l13 8-13 8z"/></svg>
                    }
                    {badge && (
                      <span
                        className={'review-clip-score score-' + badge.tier}
                        title={badge.summary || badge.label}
                        aria-label={badge.label}
                      >
                        {badge.score != null ? badge.score : '!'}
                      </span>
                    )}
                  </div>
                  <div className="review-clip-info">
                    <div className="review-clip-script">{scriptTitle(c.script_key)}</div>
                    <div className="review-clip-meta">
                      <span className={'pill ' + pill.cls}>{pill.label}</span>
                      {c.duration_seconds != null && (
                        <span className="review-clip-dur">{Math.round(c.duration_seconds)}s</span>
                      )}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </aside>

        <section className="review-main">
          {activeClip ? (
            <ClipDetail
              clip={activeClip}
              index={activeIndex}
              total={clips.length}
              scriptTitle={scriptTitle(activeClip.script_key)}
              onApprove={() => approve(activeClip.id)}
              onRejectStart={() => setShowRejectFor(activeClip.id)}
              onMarkUsed={() => markUsed(activeClip.id)}
              onUnreject={() => approve(activeClip.id)}
              busy={busy}
              showRejectFor={showRejectFor}
              rejectNote={rejectNote}
              setRejectNote={setRejectNote}
              onRejectCancel={() => { setShowRejectFor(null); setRejectNote(''); }}
              onRejectSubmit={() => submitReject(activeClip.id)}
              edits={(data.edits || []).filter((e) => e.source_clip_id === activeClip.id)}
              renders={(data.renders || []).filter((r) => r.clip_id === activeClip.id)}
              reviewer={reviewer}
              onChange={load}
            />
          ) : (
            <div className="admin-empty">
              <div className="admin-empty-text">
                <strong>No clips yet.</strong>
                <span>The customer hasn&apos;t submitted any recordings.</span>
              </div>
            </div>
          )}
        </section>
      </div>
    </main>
  );
}

// ── Admin video editor: Stream-native trim ────────────────────────────────
// Admin-only. Plays the source clip in a native <video> (MP4 rendition) so we
// can read currentTime, lets the reviewer pick in/out points, and POSTs to
// /api/clip/:id/trim which calls Cloudflare Stream's /clip API. The original
// clip + its Stream video are never modified — each trim is a new derived copy
// listed below. Customers never see any of this.
const STREAM_SUB = 'customer-zzob661ztw2o5t2h';

function uidFromClip(clip) {
  // The dashboard clip carries the uid inside one of its Stream URLs.
  for (const u of [clip.thumbnail_url, clip.watch_url, clip.stream_thumbnail_url, clip.iframe_url]) {
    if (!u) continue;
    const m = String(u).match(/cloudflarestream\.com\/([a-f0-9]{20,})/i) || String(u).match(/\/([a-f0-9]{32})(?:\/|$)/i);
    if (m) return m[1];
  }
  return clip.stream_video_uid || null;
}

const fmt = (s) => {
  if (s == null || isNaN(s)) return '0:00.0';
  const m = Math.floor(s / 60);
  const sec = (s % 60).toFixed(1).padStart(4, '0');
  return `${m}:${sec}`;
};

function editStatusBadge(e) {
  if (e.ready || e.status === 'ready') return { cls: 'pill-approved', label: 'Ready' };
  if (e.status === 'error') return { cls: 'pill-rejected', label: 'Failed' };
  return { cls: 'pill-gray', label: 'Processing…' };
}

function TrimEditor({ clip, edits = [], reviewer, onChange }) {
  const [open, setOpen] = useState(false);
  const videoRef = useRef(null);
  const uid = uidFromClip(clip);
  const mp4 = uid ? `https://${STREAM_SUB}.cloudflarestream.com/${uid}/downloads/default.mp4` : null;

  const [start, setStart] = useState(0);
  const [end, setEnd] = useState(clip.duration_seconds || 0);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');

  const setFromVideo = (which) => {
    const t = videoRef.current?.currentTime;
    if (t == null) return;
    if (which === 'start') setStart(+t.toFixed(1));
    else setEnd(+t.toFixed(1));
  };

  const create = async () => {
    setMsg('');
    if (!(end > start) || start < 0) { setMsg('End must be after start.'); return; }
    setBusy(true);
    try {
      const r = await api(`/api/clip/${clip.id}/trim`, {
        method: 'POST',
        body: JSON.stringify({
          startTimeSeconds: start,
          endTimeSeconds: end,
          name: name.trim() || undefined,
          reviewer_email: reviewer || undefined,
        }),
      });
      setMsg('Trim created — processing…');
      // Poll until the new clip is ready, then refresh the page data.
      const editId = r.edit?.id;
      if (editId) {
        for (let i = 0; i < 20; i++) {
          await new Promise((res) => setTimeout(res, 3000));
          try {
            const st = await api(`/api/clip-edit/${editId}`);
            if (st.ready) { setMsg('Trim ready.'); break; }
            if (st.status === 'error') { setMsg('Trim failed in encoding.'); break; }
          } catch { /* keep polling */ }
        }
      }
      setName('');
      onChange && onChange();
    } catch (e) {
      setMsg(e?.status === 401 ? 'Session expired — log in again.' : 'Trim failed. Try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <details className="review-trim" open={open} onToggle={(e) => setOpen(e.target.open)}>
      <summary>
        <span>Video editor · trim</span>
        <span className="review-transcript-source">admin only · makes a copy</span>
      </summary>

      <div className="trim-body">
        {mp4 ? (
          <video ref={videoRef} className="trim-video" src={mp4} controls playsInline preload="metadata"
                 poster={clip.stream_thumbnail_url || clip.thumbnail_url || undefined} />
        ) : (
          <p className="trim-hint">No playable source for this clip yet.</p>
        )}

        <div className="trim-controls">
          <div className="trim-row">
            <button type="button" className="trim-set" onClick={() => setFromVideo('start')}>Set start to current</button>
            <span className="trim-readout">In: <strong>{fmt(start)}</strong></span>
            <input type="number" step="0.1" min="0" value={start}
                   onChange={(e) => setStart(Math.max(0, +e.target.value))} className="trim-num" />
          </div>
          <div className="trim-row">
            <button type="button" className="trim-set" onClick={() => setFromVideo('end')}>Set end to current</button>
            <span className="trim-readout">Out: <strong>{fmt(end)}</strong></span>
            <input type="number" step="0.1" min="0" value={end}
                   onChange={(e) => setEnd(Math.max(0, +e.target.value))} className="trim-num" />
          </div>
          <div className="trim-row">
            <input type="text" className="trim-name" placeholder="Name (optional, e.g. 'switch — tight cut')"
                   value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div className="trim-row">
            <button type="button" className="admin-sync-btn" onClick={create} disabled={busy || !mp4}>
              {busy ? 'Creating…' : `Create trimmed copy (${fmt(Math.max(0, end - start))})`}
            </button>
            {msg && <span className="admin-sync-msg">{msg}</span>}
          </div>
          <p className="trim-hint">Tip: play the video, pause where you want, then “Set start/end to current.” The original is never changed.</p>
        </div>

        {edits.length > 0 && (
          <div className="trim-edits">
            <div className="trim-edits-title">Trimmed copies · {edits.length}</div>
            {edits.map((e) => {
              const b = editStatusBadge(e);
              return (
                <div key={e.id} className="trim-edit-row">
                  {e.thumbnail_url
                    ? <img className="trim-edit-thumb" src={e.thumbnail_url} alt="" onError={(ev) => { ev.currentTarget.style.display = 'none'; }} />
                    : <span className="trim-edit-thumb" />}
                  <div className="trim-edit-meta">
                    <span className="trim-edit-name">{e.name || 'Trim'} · {fmt(e.start_seconds)}–{fmt(e.end_seconds)}</span>
                    <span className={'pill ' + b.cls}>{b.label}</span>
                  </div>
                  {e.ready && e.watch_url && (
                    <a className="trim-edit-link" href={e.watch_url} target="_blank" rel="noreferrer">Open</a>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </details>
  );
}

const ASPECTS = [
  { key: '9:16', label: '9:16 · TikTok / Reels / Shorts' },
  { key: '1:1',  label: '1:1 · Feed' },
  { key: '4:5',  label: '4:5 · FB / IG Feed' },
  { key: '16:9', label: '16:9 · YouTube' },
];

function renderStatusBadge(r) {
  if (r.ready || r.status === 'succeeded') return { cls: 'pill-approved', label: 'Ready' };
  if (r.status === 'failed') return { cls: 'pill-rejected', label: 'Failed' };
  return { cls: 'pill-gray', label: 'Rendering…' };
}

// Admin-only. Produces a marketing-ready version of the clip via Creatomate:
// vertical (or other) reframe + burned-in auto-captions. Async render; polls
// until ready, then lists the output with a download link. Original untouched.
function MarketingEditor({ clip, renders = [], reviewer, onChange }) {
  const [aspect, setAspect] = useState('9:16');
  const [captions, setCaptions] = useState(true);
  const [branded, setBranded] = useState(true);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [act, setAct] = useState(null); // `${renderId}s` (stream) | `${renderId}f` (facebook)

  const copyToStream = async (rid) => {
    setAct(rid + 's'); setMsg('');
    try {
      await api(`/api/render/${rid}/stream`, {
        method: 'POST', body: JSON.stringify({ reviewer_email: reviewer || undefined }),
      });
      onChange && onChange();
    } catch { setMsg('Cloudflare copy failed.'); }
    finally { setAct(null); }
  };

  const sendToFacebook = async (rid) => {
    setAct(rid + 'f'); setMsg('');
    try {
      await api(`/api/render/${rid}/facebook`, {
        method: 'POST', body: JSON.stringify({ reviewer_email: reviewer || undefined }),
      });
      setMsg('Uploading to Facebook — Meta is processing it…');
      onChange && onChange();
    } catch { setMsg('Facebook upload failed.'); }
    finally { setAct(null); }
  };

  const make = async () => {
    setMsg(''); setBusy(true);
    try {
      const r = await api(`/api/clip/${clip.id}/marketing-render`, {
        method: 'POST',
        body: JSON.stringify({ aspect, captions, branded, reviewer_email: reviewer || undefined }),
      });
      setMsg('Render started — this takes ~1 min…');
      const rid = r.render?.id;
      if (rid) {
        for (let i = 0; i < 30; i++) {
          await new Promise((res) => setTimeout(res, 4000));
          try {
            const st = await api(`/api/render/${rid}`);
            if (st.ready) { setMsg('Marketing version ready.'); break; }
            if (st.status === 'failed') { setMsg('Render failed: ' + (st.error || 'unknown')); break; }
          } catch { /* keep polling */ }
        }
      }
      onChange && onChange();
    } catch (e) {
      setMsg(e?.status === 401 ? 'Session expired — log in again.'
           : e?.status === 503 ? 'Creatomate not configured.' : 'Render failed. Try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <details className="review-trim">
      <summary>
        <span>Marketing version · reframe + captions</span>
        <span className="review-transcript-source">admin only · Creatomate · makes a copy</span>
      </summary>
      <div className="trim-body">
        <div className="trim-controls">
          <div className="trim-row">
            <label className="trim-readout" htmlFor={`asp-${clip.id}`}>Format</label>
            <select id={`asp-${clip.id}`} className="trim-num" style={{ width: 'auto', flex: 1 }}
                    value={aspect} onChange={(e) => setAspect(e.target.value)}>
              {ASPECTS.map((a) => <option key={a.key} value={a.key}>{a.label}</option>)}
            </select>
          </div>
          <label className="trim-row" style={{ cursor: 'pointer' }}>
            <input type="checkbox" checked={captions} onChange={(e) => setCaptions(e.target.checked)} />
            <span className="trim-readout">Burn in auto-captions (word-by-word)</span>
          </label>
          <label className="trim-row" style={{ cursor: 'pointer' }}>
            <input type="checkbox" checked={branded} onChange={(e) => setBranded(e.target.checked)} />
            <span className="trim-readout">RavePilot branding (logo, name lower-third, CTA end card)</span>
          </label>
          <div className="trim-row">
            <button type="button" className="admin-sync-btn" onClick={make} disabled={busy}>
              {busy ? 'Rendering…' : 'Make marketing version'}
            </button>
            {msg && <span className="admin-sync-msg">{msg}</span>}
          </div>
          <p className="trim-hint">Creates a {aspect} copy{captions ? ' with captions' : ''}{branded ? ', RavePilot-branded' : ''}, ready for social. The original clip is never changed.</p>
        </div>

        {renders.length > 0 && (
          <div className="trim-edits">
            <div className="trim-edits-title">Marketing versions · {renders.length}</div>
            {renders.map((r) => {
              const b = renderStatusBadge(r);
              const ready = r.ready || r.status === 'succeeded';
              const subd = 'customer-zzob661ztw2o5t2h';
              return (
                <div key={r.id} className="trim-edit-row render-dist">
                  <div className="trim-edit-meta">
                    <span className="trim-edit-name">{r.aspect_ratio}{r.error ? ` · ${r.error}` : ''}</span>
                    <span className={'pill ' + b.cls}>{b.label}</span>
                    {r.stream_uid && <span className="pill pill-approved">Cloudflare</span>}
                  </div>
                  {ready && (
                    <div className="render-dist-actions">
                      {r.public_url && (
                        <a className="trim-edit-link" href={r.public_url} target="_blank" rel="noreferrer">Download</a>
                      )}
                      {r.stream_uid ? (
                        <a className="trim-edit-link" target="_blank" rel="noreferrer"
                           href={`https://${subd}.cloudflarestream.com/${r.stream_uid}/watch`}>Cloudflare ↗</a>
                      ) : (
                        <button type="button" className="render-dist-btn" disabled={act === r.id + 's'}
                                onClick={() => copyToStream(r.id)}>
                          {act === r.id + 's' ? 'Copying…' : 'Copy to Cloudflare'}
                        </button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </details>
  );
}

// ── Clip detail block — broken out for readability ────────────────────────
function ClipDetail({
  clip, index, total, scriptTitle,
  onApprove, onRejectStart, onMarkUsed, onUnreject, busy,
  showRejectFor, rejectNote, setRejectNote, onRejectCancel, onRejectSubmit,
  edits = [], renders = [], reviewer, onChange,
}) {
  const pill = clipPill(clip);
  const duration = clip.duration_seconds ? Math.round(clip.duration_seconds) : null;

  return (
    <>
      <div className="review-clip-head">
        <div>
          <span className="review-clip-eyebrow"><span className="bar"></span>{scriptTitle}</span>
          <h3 className="review-clip-title">Clip {index + 1} of {total}</h3>
        </div>
        <span className={'pill ' + pill.cls}>{pill.label}</span>
      </div>

      {/* Stream playback. iframe_url is the universal Cloudflare embed —
         always works once stream_ready=true. If the encode is still
         processing we show a poster card instead. */}
      <div className="review-player">
        {clip.stream_ready && clip.iframe_url ? (
          <iframe
            src={clip.iframe_url}
            allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;"
            allowFullScreen
            title={`Clip ${index + 1}: ${scriptTitle}`}
          />
        ) : (
          <div className="review-player-poster">
            {clip.stream_thumbnail_url && (
              <img src={clip.stream_thumbnail_url} alt="" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
            )}
            <div className="review-player-processing">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 12a9 9 0 11-6.219-8.56"/>
              </svg>
              <span>Encoding…</span>
            </div>
            {duration != null && (
              <span className="review-player-dur">
                0:{String(duration).padStart(2, '0')}
              </span>
            )}
          </div>
        )}
      </div>

      <VerifyCard clip={clip} />

      <TrimEditor clip={clip} edits={edits} reviewer={reviewer} onChange={onChange} />

      <MarketingEditor clip={clip} renders={renders} reviewer={reviewer} onChange={onChange} />

      {/* Transcript — Whisper output, collapsed by default if missing,
         open by default if present so reviewer can quickly skim. */}
      {clip.transcript ? (
        <details className="review-transcript" open>
          <summary>
            <span>Transcript</span>
            <span className="review-transcript-source">via Whisper</span>
          </summary>
          <p>{clip.transcript}</p>
        </details>
      ) : (
        <details className="review-transcript">
          <summary>
            <span>Transcript</span>
            <span className="review-transcript-source">not yet generated</span>
          </summary>
          <p style={{ color: 'var(--color-fg-muted)', fontStyle: 'italic' }}>
            Transcript will appear here once Whisper processes the clip.
          </p>
        </details>
      )}

      {/* Filled-script chips — what the customer typed into the blanks */}
      {clip.filled_script && Object.keys(clip.filled_script).length > 0 && (
        <details className="review-transcript" style={{ marginTop: -8 }}>
          <summary>
            <span>Filled prompts</span>
            <span className="review-transcript-source">from Fill stage</span>
          </summary>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, paddingBottom: 14 }}>
            {Object.entries(clip.filled_script).map(([k, v]) => (
              <div key={k} style={{ fontSize: 14, color: 'var(--color-fg)' }}>
                <span style={{ fontFamily: 'var(--font-utility)', fontSize: 11, color: 'var(--color-fg-quiet)', textTransform: 'uppercase', letterSpacing: '.06em', marginRight: 8 }}>{k}</span>
                {v}
              </div>
            ))}
          </div>
        </details>
      )}

      {/* ── Action row — state-dependent buttons ───────────────────────── */}
      <div className="review-actions">
        {clip.status === 'uploaded' && (
          <>
            <button className="btn btn-primary" onClick={onApprove} disabled={busy}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 6 }}>
                <polyline points="20 6 9 17 4 12"/>
              </svg>
              Approve
              <kbd className="kbd-hint">A</kbd>
            </button>
            <button className="btn btn-secondary" onClick={onRejectStart} disabled={busy}>
              Reject
              <kbd className="kbd-hint">R</kbd>
            </button>
          </>
        )}

        {clip.status === 'approved' && !clip.used_in_marketing && (
          <>
            <button className="btn btn-primary review-mark-used" onClick={onMarkUsed} disabled={busy}>
              <span className="review-star-ico" aria-hidden="true">★</span>
              Mark as used in an ad
              <kbd className="kbd-hint">U</kbd>
            </button>
            <button className="btn btn-secondary" onClick={onRejectStart} disabled={busy}>
              Reject instead
            </button>
          </>
        )}

        {clip.status === 'approved' && clip.used_in_marketing && (
          <div className="review-used-confirm">
            <span className="review-star-ico" aria-hidden="true">★</span>
            <div>
              <strong>Marked as used in an ad.</strong>
              <span>This clip is approved for use in marketing.</span>
            </div>
          </div>
        )}

        {clip.status === 'rejected' && (
          <div className="review-rejected">
            <strong>Rejected.</strong>
            {clip.reviewer_notes && <span>&ldquo;{clip.reviewer_notes}&rdquo;</span>}
            <button className="link-button" onClick={onUnreject} disabled={busy}>Un-reject</button>
          </div>
        )}

        {clip.status === 'redo_requested' && (
          <div className="review-rejected" style={{ background: '#f4f7fe', borderColor: 'var(--ravepilot-orange-100)', color: 'var(--ravepilot-orange-press)' }}>
            <strong style={{ color: 'var(--ravepilot-orange-press)' }}>Redo requested.</strong>
            {clip.reviewer_notes && <span style={{ color: 'var(--ravepilot-orange-press)' }}>&ldquo;{clip.reviewer_notes}&rdquo;</span>}
          </div>
        )}
      </div>

      {/* Inline reject form */}
      {showRejectFor === clip.id && (
        <div className="review-reject-form">
          <label className="review-reject-label">Why are you rejecting this clip?</label>
          <textarea
            className="review-reject-input"
            rows="3"
            placeholder="A short note for the customer (optional)…"
            value={rejectNote}
            onChange={(e) => setRejectNote(e.target.value)}
            autoFocus
          />
          <div className="review-reject-actions">
            <button type="button" className="btn btn-secondary" onClick={onRejectCancel}>Cancel</button>
            <button type="button" className="btn btn-primary" onClick={onRejectSubmit} disabled={busy}>
              Confirm reject
            </button>
          </div>
        </div>
      )}
    </>
  );
}

// ── VerifyCard ────────────────────────────────────────────────────────────
// Renders the AI quality verification (from clip_verifications). Displays
// a score circle, summary, facet check-pills, and issue list. Includes a
// "Re-run" button that hits POST /api/clip/:id/verify and refreshes the
// page on success.
//
// Three states it handles:
//   1. No verification yet -- shows a "Score this clip" CTA. Happens for
//      clips that finished encoding before this system shipped, or for
//      clips where the auto-chain failed silently.
//   2. Error -- the most recent attempt failed. Shows the error message
//      and offers a Retry button.
//   3. Success -- score + summary + facets + issues.
function VerifyCard({ clip }) {
  const [busy, setBusy] = useState(false);
  const [localError, setLocalError] = useState('');
  const badge = verificationBadge(clip.verification);

  async function runVerify() {
    setBusy(true);
    setLocalError('');
    try {
      await api(`/api/clip/${clip.id}/verify`, { method: 'POST' });
      // The endpoint inserts a new clip_verifications row. Reload so the
      // submission detail fetch picks up the latest -- simpler than
      // surgically merging the response into the active clip's verification
      // and trusting nothing else changed.
      window.location.reload();
    } catch (e) {
      setLocalError(e?.message || 'Verification failed');
      setBusy(false);
    }
  }

  // No verification at all yet
  if (!badge) {
    return (
      <div className="verify-card verify-card-empty">
        <div className="verify-card-left">
          <div className="verify-card-icon" aria-hidden="true">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"/>
              <line x1="12" y1="8" x2="12" y2="12"/>
              <line x1="12" y1="16" x2="12.01" y2="16"/>
            </svg>
          </div>
          <div>
            <div className="verify-card-title">AI quality check not run yet</div>
            <div className="verify-card-sub">Score this clip with Claude (~10s, ~$0.01)</div>
          </div>
        </div>
        <button className="btn btn-secondary btn-sm" onClick={runVerify} disabled={busy}>
          {busy ? 'Scoring…' : 'Run check'}
        </button>
      </div>
    );
  }

  // Error state
  if (badge.tier === 'error') {
    return (
      <div className="verify-card verify-card-error">
        <div className="verify-card-left">
          <div className="verify-card-icon" aria-hidden="true">⚠️</div>
          <div>
            <div className="verify-card-title">AI check failed</div>
            <div className="verify-card-sub">{badge.summary}</div>
          </div>
        </div>
        <button className="btn btn-secondary btn-sm" onClick={runVerify} disabled={busy}>
          {busy ? 'Retrying…' : 'Retry'}
        </button>
      </div>
    );
  }

  // Success
  const tierTitle = {
    good: 'Looks great',
    fair: 'Review carefully',
    poor: 'Likely unusable',
  }[badge.tier];

  return (
    <div className={'verify-card verify-card-' + badge.tier}>
      <div className="verify-card-head">
        <div className={'verify-score score-' + badge.tier} aria-hidden="true">
          <span className="verify-score-num">{badge.score}</span>
          <span className="verify-score-denom">/10</span>
        </div>
        <div className="verify-card-headtext">
          <div className="verify-card-tier">{tierTitle}</div>
          <div className="verify-card-summary">{badge.summary}</div>
        </div>
        <button
          className="verify-card-rerun"
          onClick={runVerify}
          disabled={busy}
          title="Re-run AI quality check"
          aria-label="Re-run verification"
        >
          {busy ? (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" className="verify-spin">
              <path d="M21 12a9 9 0 11-6.219-8.56"/>
            </svg>
          ) : (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 2v6h-6"/>
              <path d="M3 12a9 9 0 0115-6.7L21 8"/>
              <path d="M3 22v-6h6"/>
              <path d="M21 12a9 9 0 01-15 6.7L3 16"/>
            </svg>
          )}
        </button>
      </div>

      <div className="verify-facets">
        <FacetPill ok={badge.person_visible} label="Person visible" />
        <FacetPill ok={badge.audio_clear} label="Audio clear" />
        <FacetPill ok={badge.content_match} label="On script" />
      </div>

      {badge.issues.length > 0 && (
        <details className="verify-issues">
          <summary>
            <span>{badge.issues.length} {badge.issues.length === 1 ? 'concern' : 'concerns'}</span>
            <span className="verify-issues-toggle">show</span>
          </summary>
          <ul>
            {badge.issues.map((iss, i) => (
              <li key={i}>{iss}</li>
            ))}
          </ul>
        </details>
      )}

      {localError && <div className="verify-card-localerr">{localError}</div>}
    </div>
  );
}

function FacetPill({ ok, label }) {
  return (
    <span className={'verify-facet ' + (ok ? 'ok' : 'bad')}>
      {ok ? (
        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="20 6 9 17 4 12"/>
        </svg>
      ) : (
        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
          <line x1="18" y1="6" x2="6" y2="18"/>
          <line x1="6" y1="6" x2="18" y2="18"/>
        </svg>
      )}
      {label}
    </span>
  );
}
